import { generatePath } from 'react-router-dom';
import { t } from 'ttag';

import { brandFeed } from '@legacyUrls';
import { ConditionEnum } from '@models';
import { COSMETIC_CATALOG_START_PATH, SHOES_CATALOG_START_PATH } from '@constants';
import { getOutletAbsoluteUrl, getShafaAbsoluteUrl } from '@utils/common';

import { Product, Review } from './types';

interface StructuredDataMarkupPayload {
  product: Product,
  reviews: Review[],
  isOutlet?: boolean,
}

export const getStructuredDataMarkup = ({
  product,
  reviews,
  isOutlet,
}: StructuredDataMarkupPayload) => {
  return {
    '@context': 'http://schema.org/',
    '@type': 'Product',
    'url': isOutlet ? getOutletAbsoluteUrl(product.url) : getShafaAbsoluteUrl(product.url),
    'name': product.name,
    'image': [product.photos[0].thumbnailUrl],
    'description': product.description,
    'itemCondition': product.condition?.enum === ConditionEnum.New
      ? 'https://schema.org/NewCondition'
      : 'https://schema.org/UsedCondition',

    'offers': {
      '@type': 'Offer',
      'priceCurrency': 'UAH',
      'price': product.price,
      'availability': product.canBeBought ? 'http://schema.org/InStock' : 'http://schema.org/SoldOut',
      'priceValidUntil': '{{ google_price_valid_until }}', // TODO

      ...(!!product.seller.sellerRatingInfo?.overallRating && !!product.seller.sellerRatingInfo.reviewsCount && {
        'seller': {
          '@type': 'Organization',
          'name': product.seller.username,
          'aggregateRating': {
            '@type': 'AggregateRating',
            'ratingValue': product.seller.sellerRatingInfo.overallRating,
            'reviewCount': product.seller.sellerRatingInfo.reviewsCount,
          },
        },
      }),
    },

    ...(!!product.brand && {
      'brand': {
        '@type': 'Brand',
        'name': product.brand.name,
        'url': isOutlet
          ? getOutletAbsoluteUrl(generatePath(brandFeed, { slug: product.brand.slug }))
          : getShafaAbsoluteUrl(generatePath(brandFeed, { slug: product.brand.slug })),
      },
    }),

    ...(!!product.rating && !!product.ratingAmount && {
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': product.rating,
        'reviewCount': product.ratingAmount,
      },
    }),

    ...(!!reviews.length && {
      'review': reviews.map((review) => ({
        '@type': 'Review',
        'author': {
          '@type': 'Person',
          'name': review.user.username,
        },
        'datePublished': review.createdAt,
        ...(!!review.text && {
          'reviewBody': review.text,
        }),
        'reviewRating': {
          '@type': 'Rating',
          'bestRating': 5,
          'ratingValue': review.overallRating,
          'worstRating': 1,
        },
      })),
    }),
  };
};

interface OgTitlePayload {
  product: Product,
  currencyName?: string,
  siteName: string,
}

export const getOgTitle = ({
  product,
  currencyName,
  siteName,
}: OgTitlePayload) => {
  let ogTitle = product.name;
  
  if (product.brand) {
    ogTitle = `${product.name} ${product.brand.name}`;
  }

  if (product.price) {
    ogTitle = t`${ogTitle} за ${product.price} ${currencyName}`;
  }

  return `${ogTitle} | ${siteName}`;
};

interface OgDescriptionPayload {
  product: Product,
  currencyName?: string,
  siteName: string,
}

export const getOgDescription = ({
  product,
  currencyName,
  siteName,
}: OgDescriptionPayload) => {
  const catalogName = product.catalog?.name;
  const sizeNames = product.sizes.map((size) => size.name).join(', ');
  const colorNames = product.colors?.map((color) => color?.name).join(', ');
  const isCosmeticCatalog = product.catalog?.path?.startsWith(COSMETIC_CATALOG_START_PATH);
  const isShoesCatalog = product.catalog?.path?.startsWith(SHOES_CATALOG_START_PATH);

  let ogDescription = '';

  if (isCosmeticCatalog) {
    ogDescription = t`Купити ${product.name}`;

    if (product.price) {
      ogDescription = t`${ogDescription} по ціні ${product.price} ${currencyName}`;
    }

    ogDescription = t`${ogDescription} На Шафі . ✔ ${catalogName} на ${siteName} — купуй вже зараз!`;
  } else if (isShoesCatalog) {
    ogDescription = t`Купуй ${product.name} На Шафі,`;

    if (product.price) {
      ogDescription = t`${ogDescription} ціна ${product.price} ${currencyName},`;
    }

    if (product.colors?.length) {
      if (product.colors.length > 1) {
        ogDescription = t`${ogDescription} кольори —`;
      } else {
        ogDescription = t`${ogDescription} колір —`;
      }

      ogDescription = `${ogDescription} ${colorNames},`;
    }

    if (product.sizes.length) {
      ogDescription = t`${ogDescription} розміри: ${sizeNames}.`;
    }

    ogDescription = t`${ogDescription} Кращі ціни на ${product.name} і інші ${catalogName} на SHAFA.ua!`;
  } else {
    ogDescription = product.name;

    if (product.price) {
      ogDescription = t`Ціна: ${product.price} ${currencyName}. — ${ogDescription}`;
    }

    if (product.brand) {
      ogDescription = `${ogDescription} ${product.brand.name}`;
    }

    ogDescription = `${ogDescription}, #${product.id}`;

    if (product.colors?.length) {
      ogDescription = t`${ogDescription}, Кольори: ${colorNames}`;
    }

    if (product.sizes) {
      ogDescription = t`${ogDescription}, Розміри: ${sizeNames}`;
    }

    ogDescription = t`${ogDescription}. Купити на Шафі недорого`;
  }

  return ogDescription;
};
